import { PACIENTES_MOCK } from "../mocks/data";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8061";

/**
 * Normaliza un RUT para búsqueda: trim, uppercase, sin puntos.
 */
export function normalizarRut(rut) {
  return rut.trim().toUpperCase().replace(/\./g, "");
}

/**
 * Busca un paciente en PACIENTES_MOCK por RUT normalizado.
 */
export function buscarPacienteMock(rut) {
  if (!rut || !rut.trim()) return null;
  const normalizado = normalizarRut(rut);
  return PACIENTES_MOCK[normalizado] ?? null;
}

/**
 * Busca un paciente: primero en mock, luego en la API de timeline real.
 * @param {string} rut
 * @returns {Promise<{ paciente: object|null, source: "mock"|"api"|null, error: string|null }>}
 */
export async function buscarPaciente(rut) {
  if (!rut || !rut.trim()) return { paciente: null, source: null, error: null };

  const normalizado = normalizarRut(rut);

  // 1. Buscar en mock primero (respuesta instantánea)
  const mock = PACIENTES_MOCK[normalizado];
  if (mock) {
    return { paciente: mock, source: "mock", error: null };
  }

  // 2. Buscar en API real
  try {
    const resp = await fetch(`${API_BASE}/api/paciente/${encodeURIComponent(normalizado)}/timeline`, {
      signal: AbortSignal.timeout(15000),
    });

    if (!resp.ok) {
      if (resp.status === 404) {
        return { paciente: null, source: null, error: null };
      }
      const detail = await resp.text().catch(() => "Error desconocido");
      return { paciente: null, source: null, error: `API error ${resp.status}: ${detail}` };
    }

    const data = await resp.json();

    if (!data.eventos || data.eventos.length === 0) {
      return { paciente: null, source: null, error: null };
    }

    // Transformar respuesta API al formato que espera la página Paciente
    const paciente = {
      rut: data.rut,
      nombre: `Paciente ${data.rut}`,
      edad: null,
      prevision: data.convenios?.[0]?.convenio || "Sin información",
      centro_habitual: "—",
      total_eventos: data.total_eventos,
      tipo_viaje_detectado: data.tipo_viaje,
      resumen_por_ambito: data.resumen_por_ambito || [],
      eventos: data.eventos.map((e) => ({
        fecha: e.fecha,
        ambito: e.ambito,
        descripcion: e.descripcion,
        color: e.color,
        hora: e.hora || undefined,
      })),
      convenios: data.convenios || [],
      errores_api: data.errores || null,
      actividad_mensual: calcularActividadMensual(data.eventos),
      quiebres: [],
      _source: "api",
    };

    return { paciente, source: "api", error: null };
  } catch (err) {
    if (err.name === "TimeoutError" || err.name === "AbortError") {
      return { paciente: null, source: null, error: "Timeout: la API no respondió en 15 segundos. ¿Está corriendo el backend?" };
    }
    if (err.message?.includes("Failed to fetch") || err.message?.includes("NetworkError")) {
      return { paciente: null, source: null, error: "No se pudo conectar a la API (localhost:8061). ¿Está corriendo el backend?" };
    }
    return { paciente: null, source: null, error: `Error: ${err.message}` };
  }
}

/**
 * Calcula actividad mensual a partir de eventos.
 */
function calcularActividadMensual(eventos) {
  const meses = {};
  for (const e of eventos) {
    if (!e.fecha) continue;
    const mes = e.fecha.substring(0, 7); // "2026-04"
    const label = formatMes(mes);
    meses[label] = (meses[label] || 0) + 1;
  }
  return Object.entries(meses)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-6)
    .map(([mes, eventos]) => ({ mes, eventos }));
}

function formatMes(yyyymm) {
  const [y, m] = yyyymm.split("-");
  const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return `${meses[parseInt(m) - 1]} ${y}`;
}
